import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useIvrList } from '@/features/ivr/api';
import {
  useSmsWebhookList,
  useVoiceWebhookList,
} from '@/features/webhooks/api';
import {
  templateFeatures,
  type TtsFlowFormValues,
} from '../../types';

interface Props {
  values: TtsFlowFormValues;
  setValues: (patch: Partial<TtsFlowFormValues>) => void;
}

interface SmsTriggerProps {
  flag: boolean;
  setFlag: (v: boolean) => void;
  url: string;
  setUrl: (v: string) => void;
  label: string;
  description: string;
  smsWebhookFlag: boolean;
  smsWebhookOptions: Array<{ value: string; label: string }>;
}

function SmsTrigger({
  flag,
  setFlag,
  url,
  setUrl,
  label,
  description,
  smsWebhookFlag,
  smsWebhookOptions,
}: SmsTriggerProps) {
  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">{label}</div>
          <div className="text-xs text-muted-foreground">{description}</div>
        </div>
        <Switch checked={flag} onCheckedChange={setFlag} />
      </div>
      {flag && (
        <div className="space-y-2">
          {smsWebhookFlag ? (
            <Select value={url} onValueChange={setUrl}>
              <SelectTrigger>
                <SelectValue placeholder="Pick an SMS webhook" />
              </SelectTrigger>
              <SelectContent>
                {smsWebhookOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://sms-gateway.example.com/send?to={PHONE}"
              className="font-mono text-xs"
            />
          )}
        </div>
      )}
    </div>
  );
}

export function StepNotifSchedule({ values, setValues }: Props) {
  const features = templateFeatures(values.templateId);
  const smsHooks = useSmsWebhookList();
  const webhooks = useVoiceWebhookList();
  const ivrs = useIvrList();

  const smsWebhookFlag = (smsHooks.data ?? []).length > 0;
  // The legacy stored a JSON string of `{url, smsText, requestType, payload,
  // header}` as the dropdown value — preserve that round-trip.
  const smsOptions = (smsHooks.data ?? []).map((w) => ({
    value: JSON.stringify({
      url: (w as { url?: string }).url ?? '',
      smsText: (w as { smsText?: string }).smsText ?? '',
      requestType: (w as { requestType?: string }).requestType ?? 'GET',
      payload: (w as { payload?: string }).payload ?? '',
      header: (w as { header?: string }).header ?? '',
    }),
    label: w.webhookName,
  }));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>SMS triggers</CardTitle>
          <CardDescription>
            Send an SMS to the caller on call outcome. Uses an SMS webhook if
            you have one configured, otherwise paste a gateway URL with
            <code className="mx-1 rounded bg-muted px-1 text-xs">{'{PHONE}'}</code>
            as the destination placeholder.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <SmsTrigger
            flag={values.sendSmsSuccess}
            setFlag={(v) => setValues({ sendSmsSuccess: v })}
            url={values.smsApiSuccess}
            setUrl={(v) => setValues({ smsApiSuccess: v })}
            label="SMS on success"
            description="Sent when the call meets the success duration below."
            smsWebhookFlag={smsWebhookFlag}
            smsWebhookOptions={smsOptions}
          />
          {values.sendSmsSuccess && (
            <div className="space-y-2 rounded-md border border-border p-3">
              <Label htmlFor="cd-sms">
                Min call duration for success SMS (seconds)
              </Label>
              <Input
                id="cd-sms"
                inputMode="numeric"
                value={String(values.callDurationSMS)}
                onChange={(e) =>
                  setValues({
                    callDurationSMS:
                      Number(e.target.value.replace(/\D/g, '')) || 0,
                  })
                }
              />
            </div>
          )}
          <SmsTrigger
            flag={values.sendSmsFail}
            setFlag={(v) => setValues({ sendSmsFail: v })}
            url={values.smsApiFail}
            setUrl={(v) => setValues({ smsApiFail: v })}
            label="SMS on failure"
            description="Sent when the call doesn't connect or hangs up too early."
            smsWebhookFlag={smsWebhookFlag}
            smsWebhookOptions={smsOptions}
          />
          {features.hasSmsDtmf && (
            <SmsTrigger
              flag={values.sendSmsDtmf}
              setFlag={(v) => setValues({ sendSmsDtmf: v })}
              url={values.smsApiDtmf}
              setUrl={(v) => setValues({ smsApiDtmf: v })}
              label="SMS on DTMF input"
              description="Sent when the caller presses a valid DTMF digit."
              smsWebhookFlag={smsWebhookFlag}
              smsWebhookOptions={smsOptions}
            />
          )}
          {features.hasPatchSms && (
            <>
              <SmsTrigger
                flag={values.sendSmsPatchSuccess}
                setFlag={(v) => setValues({ sendSmsPatchSuccess: v })}
                url={values.callPatchSuccessMessage}
                setUrl={(v) => setValues({ callPatchSuccessMessage: v })}
                label="SMS to agent on patch success"
                description="Sent to the agent group when the call was successfully patched."
                smsWebhookFlag={smsWebhookFlag}
                smsWebhookOptions={smsOptions}
              />
              <SmsTrigger
                flag={values.sendSmsPatchFail}
                setFlag={(v) => setValues({ sendSmsPatchFail: v })}
                url={values.callPatchFailMessage}
                setUrl={(v) => setValues({ callPatchFailMessage: v })}
                label="SMS to agent on patch fail"
                description="Sent when the patch attempt failed (no agent answered)."
                smsWebhookFlag={smsWebhookFlag}
                smsWebhookOptions={smsOptions}
              />
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Webhook & IVR (optional)</CardTitle>
          <CardDescription>
            Wire a campaign-level webhook event or link a Custom IVR flow.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2 rounded-md border border-border p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Enable webhook</div>
                <div className="text-xs text-muted-foreground">
                  Fire a webhook on campaign events.
                </div>
              </div>
              <Switch
                checked={values.sendWebhook}
                onCheckedChange={(v) => setValues({ sendWebhook: v })}
              />
            </div>
            {values.sendWebhook && (
              <Select
                value={values.webhookId}
                onValueChange={(v) => setValues({ webhookId: v })}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      webhooks.isLoading
                        ? 'Loading…'
                        : (webhooks.data ?? []).length === 0
                          ? 'No webhooks configured'
                          : 'Pick a webhook'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {(webhooks.data ?? []).map((w) => (
                    <SelectItem key={String(w.id)} value={String(w.id)}>
                      {w.webhookName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2 rounded-md border border-border p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Link a Custom IVR</div>
                <div className="text-xs text-muted-foreground">
                  Hand the call off to one of your Custom IVR flows.
                </div>
              </div>
              <Switch
                checked={values.useIvr}
                onCheckedChange={(v) => setValues({ useIvr: v })}
              />
            </div>
            {values.useIvr && (
              <Select
                value={values.ivrId}
                onValueChange={(v) => setValues({ ivrId: v })}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      ivrs.isLoading
                        ? 'Loading…'
                        : (ivrs.data ?? []).length === 0
                          ? 'No IVRs configured'
                          : 'Pick an IVR'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {(ivrs.data ?? [])
                    .filter((f) => String(f.status ?? '1') === '1')
                    .map((f) => (
                      <SelectItem key={f.id} value={String(f.id)}>
                        {f.flowName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Schedule</CardTitle>
          <CardDescription>
            Date range + daily start/end times the campaign is active.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="start-date">
              Start date
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="start-date"
              type="date"
              value={values.startDate}
              onChange={(e) => setValues({ startDate: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="end-date">
              End date
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="end-date"
              type="date"
              value={values.endDate}
              onChange={(e) => setValues({ endDate: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="start-time">
              Daily start time
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="start-time"
              type="time"
              value={values.startTime}
              onChange={(e) => setValues({ startTime: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="end-time">
              Daily end time
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="end-time"
              type="time"
              value={values.endTime}
              onChange={(e) => setValues({ endTime: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
